import React, { useState, useRef } from 'react';
import SafeIcon from '../common/SafeIcon';

const Settings = () => {
  const fileLightRef = useRef(null);
  const fileDarkRef = useRef(null);
  
  const [logos, setLogos] = useState({
    light: 'https://media-manager-c.questera.ai/greta-media/3140d8d6cb9117fc579edbc74f55e47991f06b3358b3a2dbff9e876f24bde08089c96c4a7bef32e6eede9d743c938dc3/images/aW1hZ2Uvd2VicA==/87ef84ef85cdd0abb6871e76d9985670.webp',
    dark: 'https://media-manager-c.questera.ai/greta-media/3140d8d6cb9117fc579edbc74f55e47991f06b3358b3a2dbff9e876f24bde08089c96c4a7bef32e6eede9d743c938dc3/images/aW1hZ2Uvd2VicA==/87ef84ef85cdd0abb6871e76d9985670.webp'
  });

  // Taxonomies State
  const [departments, setDepartments] = useState(['Administration', 'Engineering', 'Marketing', 'Operations']);
  const [jobTypes, setJobTypes] = useState(['Full-time', 'Part-time', 'Contract', 'Internship']);
  const [newDept, setNewDept] = useState('');
  const [newJobType, setNewJobType] = useState('');

  const handleLogoUpload = (type, e) => {
    const file = e.target.files[0];
    if (file) {
      setLogos({ ...logos, [type]: URL.createObjectURL(file) });
    }
  };

  const addDepartment = () => {
    if (newDept.trim() && !departments.includes(newDept.trim())) {
      setDepartments([...departments, newDept.trim()]);
      setNewDept('');
    }
  };

  const addJobType = () => {
    if (newJobType.trim() && !jobTypes.includes(newJobType.trim())) {
      setJobTypes([...jobTypes, newJobType.trim()]);
      setNewJobType('');
    }
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Portal Settings</h1>
        <p className="text-gray-500">Configure global platform preferences and branding.</p>
      </div>

      <div className="card p-10 space-y-12">
        {/* Logo Management */}
        <section className="space-y-8">
          <div className="flex items-center justify-between border-b border-gray-50 pb-4">
            <h3 className="text-lg font-bold text-[#11469A] uppercase tracking-widest">Brand Assets</h3>
            <span className="text-xs font-bold text-gray-400">Supported: PNG, SVG, WEBP</span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div className="space-y-4">
              <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Light Version Logo</label>
              <div className="border-2 border-dashed border-gray-200 rounded-2xl p-8 bg-gray-50 flex flex-col items-center justify-center text-center group hover:border-[#11469A] transition-all relative">
                <div className="bg-white p-4 rounded-xl shadow-sm mb-4">
                  <img src={logos.light} className="h-12 w-auto object-contain" alt="Light Logo" />
                </div>
                <button 
                  onClick={() => fileLightRef.current?.click()}
                  className="text-xs font-bold text-[#11469A] hover:underline uppercase tracking-widest relative z-10"
                >
                  Replace Logo
                </button>
                <input 
                  type="file" 
                  ref={fileLightRef} 
                  onChange={(e) => handleLogoUpload('light', e)} 
                  className="hidden" 
                  accept="image/*" 
                />
              </div>
            </div>

            <div className="space-y-4">
              <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Dark Version Logo</label>
              <div className="border-2 border-dashed border-gray-200 rounded-2xl p-8 bg-gray-50 flex flex-col items-center justify-center text-center group hover:border-[#11469A] transition-all relative">
                <div className="bg-gray-800 p-4 rounded-xl shadow-sm mb-4">
                  <img src={logos.dark} className="h-12 w-auto object-contain brightness-0 invert" alt="Dark Logo" />
                </div>
                <button 
                  onClick={() => fileDarkRef.current?.click()}
                  className="text-xs font-bold text-[#11469A] hover:underline uppercase tracking-widest relative z-10"
                >
                  Replace Logo
                </button>
                <input 
                  type="file" 
                  ref={fileDarkRef} 
                  onChange={(e) => handleLogoUpload('dark', e)} 
                  className="hidden" 
                  accept="image/*" 
                />
              </div>
            </div>
          </div>
        </section>

        {/* Global SEO */}
        <section className="space-y-8">
          <div className="border-b border-gray-50 pb-4">
            <h3 className="text-lg font-bold text-[#11469A] uppercase tracking-widest">Global SEO & Identity</h3>
          </div>
          <div className="space-y-6">
            <div className="w-full flex flex-col">
              <label className="text-xs font-bold text-gray-400 uppercase tracking-widest block mb-2">Portal Name</label>
              <input type="text" defaultValue="RMGhana Talent Cloud" className="input-field w-full focus:border-[#11469A]" />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 w-full">
              <div className="w-full flex flex-col space-y-2">
                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest block">Meta Description</label>
                <textarea 
                  rows={4} 
                  className="input-field w-full resize-none text-[13px] focus:border-[#11469A]" 
                  defaultValue="Resources and Manpower Ghana - Leading recruitment and talent management portal for West Africa." 
                />
              </div>
              <div className="w-full flex flex-col space-y-2">
                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest block">Keywords</label>
                <textarea 
                  rows={4} 
                  className="input-field w-full resize-none text-[13px] focus:border-[#11469A]" 
                  defaultValue="Recruitment Ghana, HR Portal, Jobs in Accra, Workforce Management" 
                />
              </div>
            </div>
          </div>
        </section>

        {/* Job Attributes & Taxonomies */}
        <section className="space-y-8">
          <div className="border-b border-gray-50 pb-4">
            <h3 className="text-lg font-bold text-[#11469A] uppercase tracking-widest">Job Attributes & Taxonomies</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div className="space-y-4">
              <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Departments</label>
              <div className="flex gap-2">
                <input 
                  type="text" 
                  value={newDept} 
                  onChange={(e) => setNewDept(e.target.value)} 
                  placeholder="Add new department" 
                  className="input-field focus:border-[#11469A]" 
                  onKeyDown={(e) => e.key === 'Enter' && addDepartment()}
                />
                <button onClick={addDepartment} className="bg-gray-100 hover:bg-gray-200 text-gray-600 px-4 rounded-lg font-bold transition-colors">
                  Add
                </button>
              </div>
              <div className="bg-gray-50 rounded-xl border border-gray-100 p-2 max-h-48 overflow-y-auto">
                {departments.map((dept, idx) => (
                  <div key={idx} className="flex justify-between items-center p-2 hover:bg-white rounded group transition-colors">
                    <span className="text-sm text-gray-700 font-medium">{dept}</span>
                    <button onClick={() => setDepartments(departments.filter(d => d !== dept))} className="text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity">
                      <SafeIcon name="X" size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Job Types</label>
              <div className="flex gap-2">
                <input 
                  type="text" 
                  value={newJobType} 
                  onChange={(e) => setNewJobType(e.target.value)} 
                  placeholder="Add new job type" 
                  className="input-field focus:border-[#11469A]" 
                  onKeyDown={(e) => e.key === 'Enter' && addJobType()}
                />
                <button onClick={addJobType} className="bg-gray-100 hover:bg-gray-200 text-gray-600 px-4 rounded-lg font-bold transition-colors">
                  Add
                </button>
              </div>
              <div className="bg-gray-50 rounded-xl border border-gray-100 p-2 max-h-48 overflow-y-auto">
                {jobTypes.map((type, idx) => (
                  <div key={idx} className="flex justify-between items-center p-2 hover:bg-white rounded group transition-colors">
                    <span className="text-sm text-gray-700 font-medium">{type}</span>
                    <button onClick={() => setJobTypes(jobTypes.filter(t => t !== type))} className="text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity">
                      <SafeIcon name="X" size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <div className="pt-8 border-t border-gray-50">
          <button className="bg-[#11469A] hover:bg-[#0d3678] text-white font-bold h-12 px-10 rounded-lg shadow-md transition-all uppercase tracking-widest text-xs">
            Save System Configurations
          </button>
        </div>
      </div>
    </div>
  );
};

export default Settings;